/**
 * The seam.
 *
 * This interface speaks in the SPEC's messages, not in our ops. That is the
 * whole reason it exists as its own file rather than being folded into
 * `surfaceStore`: if a mature v1.0 renderer ever appears — written by somebody
 * else, or by us for a second assistant — it slots in behind this interface
 * without a line changing in `Board.tsx` or in the transport.
 *
 * The envelope stays outside it. `seq`/`rev` are OUR transport's metadata and no
 * standard processor would know what to do with them, so the gating happens in
 * `applyFrame` and what crosses the seam is a list of plain A2UI messages.
 */

import { readEnvelope, type AgentToRenderer } from './types';

export interface MessageProcessor {
  /** Applies one batch atomically, in order. */
  apply(messages: AgentToRenderer[]): void;
}

/** What a frame did, so the caller can log or count without re-reading it. */
export type FrameOutcome =
  | { applied: true; seq: number; rev: number; messages: number }
  | { applied: false; reason: 'not-a2ui' | 'replayed' };

export type FrameGate = {
  /** The highest `seq` already applied on this connection. */
  lastSeq: number;
  onApplied?: (seq: number, rev: number) => void;
};

/**
 * Applies one `custom` frame to a processor, honouring the transport metadata.
 *
 * **The `seq` gate is why the envelope is not the processor's business.** A
 * reconnect replays frames byte for byte (`Last-Event-ID`), and a frame already
 * applied must be skipped. Applying it twice is harmless anyway — every id is
 * derived from the data, so an upsert is idempotent — but «harmless» is a
 * property of today's messages, and skipping is a property of the transport.
 * Both are worth having: the fixtures pin them together.
 */
export function applyFrame(
  processor: MessageProcessor,
  data: unknown,
  gate: FrameGate,
): FrameOutcome {
  const envelope = readEnvelope(data);
  if (!envelope) return { applied: false, reason: 'not-a2ui' };
  if (envelope.seq > 0 && envelope.seq <= gate.lastSeq) {
    return { applied: false, reason: 'replayed' };
  }
  processor.apply(envelope.payload.messages);
  gate.lastSeq = Math.max(gate.lastSeq, envelope.seq);
  gate.onApplied?.(envelope.seq, envelope.rev);
  return {
    applied: true,
    seq: envelope.seq,
    rev: envelope.rev,
    messages: envelope.payload.messages.length,
  };
}
